<script setup lang="ts">
export type NavigationItem = {
  icon?: string
  iconPrefix?: string
  label: string
  to?: string
  children?: NavigationItem[]
}

const {
  as = 'nav',
  items,
  ui = undefined
} = defineProps<{
  as?: 'nav' | 'div'
  items: NavigationItem[]
  ui?: {
    item?: string
  }
}>()

const emit = defineEmits(['navigate'])

const expandedItems = ref<Record<string, boolean>>({})

const toggleExpanded = (label: string) => {
  expandedItems.value[label] = !expandedItems.value[label]
}

const handleNavigate = () => {
  emit('navigate')
}
</script>

<template>
  <Motion
    :tag="as"
    class="space-y-1"
    initial="hidden"
    animate="visible"
    exit="hidden"
    :variants="{
      hidden: {
        opacity: 0,
        transition: {
          staggerChildren: 0.025,
          staggerDirection: -1
        }
      },
      visible: {
        opacity: 1,
        transition: {
          delayChildren: 0.1,
          staggerChildren: 0.1
        }
      }
    }"
  >
    <template
      v-for="item in items"
      :key="item.label"
    >
      <Motion
        v-if="item.to"
        :variants="{
          hidden: { opacity: 0, x: -20 },
          visible: { opacity: 1, x: 0 }
        }"
      >
        <NuxtLink
          :to="item.to"
          :class="$cn('flex items-center space-x-3 rounded-md px-3 py-2 font-medium text-paragraph transition-all duration-200', ui?.item)"
          @click="handleNavigate"
        >
          <Icon
            v-if="item.icon"
            :icon="item.icon"
            :prefix="item.iconPrefix || 'far'"
            class="size-6 shrink-0"
          />
          <span>{{ item.label }}</span>
        </NuxtLink>
      </Motion>

      <Motion
        v-else-if="item.children && item.children.length > 0"
        :variants="{
          hidden: { opacity: 0, x: -20 },
          visible: { opacity: 1, x: 0 }
        }"
      >
        <button
          :class="$cn('flex w-full items-center justify-between space-x-3 rounded-md px-3 py-2 transition-all duration-200', ui?.item)"
          @click="toggleExpanded(item.label)"
        >
          <div class="flex items-center space-x-3 font-medium text-paragraph">
            <Icon
              v-if="item.icon"
              :icon="item.icon"
              :prefix="item.iconPrefix || 'far'"
              class="size-6 shrink-0"
            />
            <span>{{ item.label }}</span>
          </div>
          <Icon
            icon="chevron-down"
            prefix="fas"
            :class="{ 'rotate-180': expandedItems[item.label] }"
            class="size-6 shrink-0 transition-transform duration-200 ease-in-out"
          />
        </button>

        <div
          :class="[
            'ml-3 overflow-hidden pl-6 transition-all duration-200 ease-in-out',
            expandedItems[item.label] ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          ]"
        >
          <div class="-mx-[1px] border-l-2 border-gray-200">
            <AnimatePresence mode="wait">
              <NavigationMenu
                v-if="expandedItems[item.label]"
                :key="`${item.label}-children`"
                as="div"
                :items="item.children"
                :ui="ui"
                @navigate="handleNavigate"
              />
            </AnimatePresence>
          </div>
        </div>
      </Motion>
    </template>
  </Motion>
</template>
